// Mobile permission handler
// Supports legacy mobile payloads like { admin: ["*"] | [hotelIds] }
// and web payloads: number | { gnh: number } | Array<{ hotelId: number|string, permission: number }>

export type PermissionToken = number | { gnh: number } | Array<HotelPermission>;

export interface HotelLike {
  hotelId: string | number;
  name?: string;
}

interface HotelPermission {
  hotelId: number;
  permission: number;
  notification?: boolean | null;
}

const MIN_PERMISSION_YIELD_SHEET = 10; // Mobile only needs level >= 10
const MIN_PERMISSION = 5;

function safeParsePermissions(raw: string): PermissionToken | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as PermissionToken;
  } catch {
    return null;
  }
}

function isAllHotelsAccess(perm: PermissionToken | null): boolean {
  if (perm == null) return false;

  // Web: object with gnh
  if (typeof perm === "object" && !Array.isArray(perm) && "gnh" in perm) {
    return (perm as { gnh: number }).gnh >= MIN_PERMISSION;
  }

  // Web: legacy global number
  if (typeof perm === "number") {
    return perm >= MIN_PERMISSION;
  }

  // Mobile legacy: { admin: ["*"] }
  if (
    typeof perm === "object" &&
    !Array.isArray(perm) &&
    "admin" in perm &&
    Array.isArray((perm as any).admin)
  ) {
    const admin = (perm as any).admin as Array<any>;
    return admin.length > 0 && admin[0] === "*";
  }

  return false;
}

export function hasPermissionYieldSheet(
  perm: PermissionToken | null,
  hotelId: number,
): boolean {
  if (perm == null) return false;

  // Web: object with gnh
  if (typeof perm === "object" && !Array.isArray(perm) && "gnh" in perm) {
    return (perm as { gnh: number }).gnh >= MIN_PERMISSION_YIELD_SHEET;
  }

  // Web: legacy global number
  if (typeof perm === "number") {
    return perm >= MIN_PERMISSION_YIELD_SHEET;
  }

  // Mobile legacy: { admin: [ids] }
  if (Array.isArray(perm)) {
    const hotelPermission = perm.find(
      (p: HotelPermission) => p.hotelId === hotelId,
    );
    if (hotelPermission) {
      return hotelPermission.permission >= MIN_PERMISSION_YIELD_SHEET;
    }
  }

  return false;
}

function normalizeId(id: string | number): string {
  return String(id);
}

function extractAllowedHotelIds(
  perm: PermissionToken | null,
): Set<string> | null {
  if (perm == null) return null;

  // If all-hotels, return null to signal no filtering
  if (isAllHotelsAccess(perm)) return null;

  // Web: array of { hotelId, permission }
  if (Array.isArray(perm)) {
    const set = new Set<string>();
    perm.forEach((p) => {
      if (p && typeof p === "object") {
        const allowed =
          (p as any).permission == null ||
          (p as any).permission >= MIN_PERMISSION;
        if (allowed && (p as any).hotelId != null) {
          set.add(normalizeId((p as any).hotelId));
        }
      } else if (typeof p === "string" || typeof p === "number") {
        // Fallback: array of bare hotel IDs
        set.add(normalizeId(p as any));
      }
    });
    return set;
  }

  // Mobile legacy: { admin: [ids] }
  if (typeof perm === "object" && !Array.isArray(perm) && "admin" in perm) {
    const admin = (perm as any).admin as Array<any> | undefined;
    if (Array.isArray(admin)) {
      const set = new Set<string>();
      admin.forEach((h) => {
        if (h === "*") return; // already handled by isAllHotelsAccess
        if (typeof h === "object" && h !== null && "hotelId" in h) {
          const allowed =
            (h as any).permission == null ||
            (h as any).permission >= MIN_PERMISSION;
          if (allowed) set.add(normalizeId((h as any).hotelId));
        } else if (typeof h === "string" || typeof h === "number") {
          set.add(normalizeId(h));
        }
      });
      return set;
    }
  }

  // Web number below threshold or unknown shapes -> no access
  return new Set<string>();
}

export function getAccessibleHotels<T extends HotelLike>(
  rawPermissions: string,
  hotels: T[],
): { hotels: T[]; allAccess: boolean } {
  const parsed = safeParsePermissions(rawPermissions);
  const allAccess = isAllHotelsAccess(parsed);

  if (allAccess) {
    return { hotels: hotels ?? [], allAccess: true };
  }

  const allowedIds = extractAllowedHotelIds(parsed);
  // If null, means all-access (handled), if empty set -> none
  if (!allowedIds) {
    return { hotels: hotels ?? [], allAccess: true };
  }

  const filtered = (hotels ?? []).filter((h) =>
    allowedIds.has(normalizeId(h.hotelId)),
  );
  return { hotels: filtered, allAccess: false };
}

export function canAccessHotel(
  rawPermissions: string,
  hotelId: string | number,
): boolean {
  const parsed = safeParsePermissions(rawPermissions);
  if (isAllHotelsAccess(parsed)) return true;
  const allowed = extractAllowedHotelIds(parsed);
  if (!allowed) return true; // null signals all-access
  return allowed.has(normalizeId(hotelId));
}
